'use strict';

/**
 * Navigation structure for the AUI docs site.
 *
 * This is the single source of truth for the sidebar navigation.
 *
 * Each entry has:
 *   - group {string}  — section heading shown in the sidebar
 *   - items {Array}   — ordered list of pages in that section
 *     - path  {string} — page path relative to the site root (no leading slash)
 *     - label {string} — human-readable link text (may contain HTML, e.g. <span>)
 *     - id    {string} — anchor element id attribute
 */
module.exports = [
    {
        group: 'General UI components',
        items: [
            { path: 'docs/avatars.html', label: 'Avatars', id: 'avatars-link' },
            { path: 'docs/badges.html', label: 'Badges', id: 'badges-link' },
            { path: 'docs/banner.html', label: 'Banners', id: 'banner-link' },
            { path: 'docs/buttons.html', label: 'Buttons', id: 'buttons-link' },
            { path: 'docs/dropdown.html', label: 'Dropdowns', id: 'dropdown-link' },
            { path: 'docs/dialog2.html', label: 'Dialog 2', id: 'dialog2-link' },
            { path: 'docs/expander.html', label: 'Expanders', id: 'expander-link' },
            { path: 'docs/flag.html', label: 'Flags', id: 'flag-link' },
            { path: 'docs/inline-dialog.html', label: 'Inline dialogs', id: 'inline-dialog-link' },
            { path: 'docs/icons.html', label: 'Icons', id: 'icons-link' },
            { path: 'docs/labels.html', label: 'Labels', id: 'labels-link' },
            { path: 'docs/lozenges.html', label: 'Lozenges', id: 'lozenges-link' },
            { path: 'docs/messages.html', label: 'Messages', id: 'messages-link' },
            {
                path: 'docs/progress-indicator.html',
                label: 'Progress indicators',
                id: 'progress-indicator-link',
            },
            {
                path: 'docs/progress-tracker.html',
                label: 'Progress trackers',
                id: 'progress-tracker-link',
            },
            { path: 'docs/restful-table.html', label: 'RESTful tables', id: 'restful-table-link' },
            { path: 'docs/spinner.html', label: 'Spinners', id: 'spinner-link' },
            {
                path: 'docs/sortable-table.html',
                label: 'Sortable tables',
                id: 'sortable-table-link',
            },
            { path: 'docs/tables.html', label: 'Tables', id: 'tables-link' },
            { path: 'docs/tabs.html', label: 'Tabs', id: 'tabs-link' },
            { path: 'docs/toolbar2.html', label: 'Toolbar 2', id: 'toolbar2-link' },
            { path: 'docs/tooltips.html', label: 'Tooltips', id: 'tooltips-link' },
            { path: 'docs/trigger.html', label: 'Triggers', id: 'trigger-link' },
        ],
    },
    {
        group: 'Forms and form components',
        items: [
            { path: 'docs/forms.html', label: 'Forms', id: 'forms-link' },
            { path: 'docs/aui-label.html', label: 'Form labels', id: 'aui-label-link' },
            {
                path: 'docs/form-notification.html',
                label: 'Form notification',
                id: 'form-notification-link',
            },
            {
                path: 'docs/form-validation.html',
                label: 'Form validation',
                id: 'form-validation-link',
            },
            { path: 'docs/date-picker.html', label: 'Date pickers', id: 'date-picker-link' },
            { path: 'docs/single-select.html', label: 'Single selects', id: 'single-select-link' },
            { path: 'docs/auiselect2.html', label: 'Select2', id: 'auiselect2-link' },
            { path: 'docs/toggle-button.html', label: 'Toggle buttons', id: 'toggle-button-link' },
        ],
    },
    {
        group: 'Page and content structure',
        items: [
            { path: 'docs/typography.html', label: 'Typography', id: 'typography-link' },
            { path: 'docs/page.html', label: 'Page layout', id: 'page-link' },
            { path: 'docs/page-header.html', label: 'Page headers', id: 'page-header-link' },
            {
                path: 'docs/page-content-layout.html',
                label: 'Page panels',
                id: 'page-content-layout-link',
            },
            { path: 'docs/layout.html', label: 'Content groups', id: 'layout-link' },
            { path: 'docs/navigation.html', label: 'Navigation patterns', id: 'navigation-link' },
            {
                path: 'docs/in-product-help.html',
                label: 'In-product help patterns',
                id: 'in-product-help-link',
            },
            { path: 'docs/app-header.html', label: 'Application header', id: 'header-link' },
            { path: 'docs/sidebar.html', label: 'Sidebar', id: 'sidebar-link' },
            {
                path: 'docs/system-notifications.html',
                label: 'System notification',
                id: 'system-notifications-link',
            },
        ],
    },
    {
        group: 'AUI resources',
        items: [
            { path: 'docs/dark-mode-old.html', label: 'Dark mode (Old)', id: 'dark-mode-old-link' },
            { path: 'docs/dark-mode.html', label: 'Dark mode', id: 'dark-mode-link' },
            { path: 'docs/aui-colors.html', label: 'AUI colors', id: 'aui-colors-link' },
            { path: 'docs/helper.html', label: 'Helper functions', id: 'helper-link' },
            {
                path: 'docs/hidden-assistive-css.html',
                label: 'Hidden and assistive CSS',
                id: 'hidden-assistive-css-link',
            },
            { path: 'docs/browser-focus.html', label: 'Browser focus', id: 'browser-focus' },
            {
                path: 'docs/internationalisation.html',
                label: 'Internationalisation',
                id: 'internationalisation-link',
            },
            {
                path: 'docs/keyboard-shortcuts.html',
                label: 'Keyboard shortcuts',
                id: 'keyboard-link',
            },
            { path: 'docs/soy.html', label: 'Soy', id: 'soy-link' },
            {
                path: 'docs/template.html',
                label: 'Template <span class="aui-lozenge aui-lozenge-removed">deprecated</span>',
                id: 'template-link',
            },
        ],
    },
];
